import { useEffect, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';

type TrustedDevice = {
  id: string;
  name?: string | null;
};

type CognitoUserWithDevices = {
  getCachedDeviceKeyAndPassword?: () => void;
  forgetSpecificDevice?: (
    deviceKey: string,
    callbacks: {
      onSuccess: () => void;
      onFailure: (error: unknown) => void;
    },
  ) => void;
  deviceKey?: string | null;
  signInUserSession?: { deviceKey?: string | null };
};

const resolveDeviceKey = (user: CognitoUserWithDevices): string | undefined => {
  if (typeof user.getCachedDeviceKeyAndPassword === 'function') {
    user.getCachedDeviceKeyAndPassword();
  }

  const key = user.deviceKey ?? user.signInUserSession?.deviceKey;
  return typeof key === 'string' && key.length > 0 ? key : undefined;
};

export function useTrustedDevice() {
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentDeviceId, setCurrentDeviceId] = useState<string | undefined>();
  const [refreshCounter, setRefreshCounter] = useState(0);

  const inFlightRef = useRef(false);

  useEffect(() => {
    let isActive = true;

    const loadDevices = async () => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;

      setLoading(true);
      try {
        const user = (await Auth.currentAuthenticatedUser()) as CognitoUserWithDevices;
        const key = resolveDeviceKey(user);
        const list = (await Auth.fetchDevices()) as TrustedDevice[] | undefined;

        if (!isActive) {
          return;
        }

        setCurrentDeviceId(key);
        setDevices(list ?? []);
      } catch {
        if (!isActive) {
          return;
        }
        setDevices([]);
        setCurrentDeviceId(undefined);
      } finally {
        if (isActive) {
          setLoading(false);
        }
        inFlightRef.current = false;
      }
    };

    void loadDevices();

    return () => {
      isActive = false;
    };
  }, [refreshCounter]);

  const requestRefresh = () => {
    if (inFlightRef.current) {
      return;
    }
    setRefreshCounter(c => c + 1);
  };

  const forgetDevice = async (deviceId: string) => {
    if (!deviceId) {
      return;
    }

    if (deviceId === currentDeviceId) {
      await Auth.forgetDevice();
      requestRefresh();
      return;
    }

    const user = (await Auth.currentUserPoolUser()) as CognitoUserWithDevices;

    await new Promise<void>((resolve, reject) => {
      if (!user.forgetSpecificDevice) {
        resolve();
        return;
      }

      user.forgetSpecificDevice(deviceId, {
        onSuccess: () => resolve(),
        onFailure: error => reject(error),
      });
    });

    requestRefresh();
  };

  return {
    devices,
    loading,
    currentDeviceId,
    forgetDevice,
  };
}
